import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Icons from 'react-feather';
import DataTable from 'react-data-table-component';
import { ReactComponent as SortArrow } from '../../../shared/img/chevron-up-down.svg';
import {
  getTabTypeHelp,
  getTabTypeLabel,
  normalizeDetailTabsFromApi,
} from './utils/productDetailTabKeys';

function ProductDetailTabsTab({
  detailTabs = [],
  tabTypes = [],
  readOnly = false,
  onTabsChange,
  detailTabsSaveToken = 0,
}) {
  const [tabRows, setTabRows] = useState(() => normalizeDetailTabsFromApi(detailTabs, tabTypes));
  const [orderDirty, setOrderDirty] = useState(false);
  const [selectedTabKey, setSelectedTabKey] = useState(null);

  useEffect(() => {
    if (detailTabsSaveToken > 0) {
      setOrderDirty(false);
    }
  }, [detailTabsSaveToken]);

  useEffect(() => {
    if (orderDirty) return;
    const sorted = normalizeDetailTabsFromApi(detailTabs, tabTypes);
    setTabRows(sorted);
    if (selectedTabKey && !sorted.some((row) => row.TabKey === selectedTabKey)) {
      setSelectedTabKey(null);
    }
  }, [detailTabs, tabTypes, orderDirty, selectedTabKey]);

  useEffect(() => {
    if (!onTabsChange) return;
    onTabsChange({
      tabs: tabRows,
      dirty: orderDirty,
    });
  }, [tabRows, orderDirty, onTabsChange]);

  const selectedIndex = useMemo(
    () => tabRows.findIndex((row) => row.TabKey === selectedTabKey),
    [tabRows, selectedTabKey],
  );

  const canMoveUp = selectedIndex > 0;
  const canMoveDown = selectedIndex >= 0 && selectedIndex < tabRows.length - 1;

  const selectRow = useCallback((tabKey) => {
    setSelectedTabKey(tabKey);
  }, []);

  const markDirtyUpdate = useCallback((updater) => {
    setTabRows(updater);
    setOrderDirty(true);
  }, []);

  const handleVisibleChange = useCallback((tabKey, checked) => {
    markDirtyUpdate((prev) =>
      prev.map((row) => (row.TabKey === tabKey ? { ...row, IsVisible: checked } : row)),
    );
  }, [markDirtyUpdate]);

  const handleTitleChange = useCallback((tabKey, title) => {
    markDirtyUpdate((prev) =>
      prev.map((row) => (row.TabKey === tabKey ? { ...row, TabTitle: title } : row)),
    );
  }, [markDirtyUpdate]);

  const moveSelected = useCallback((direction) => {
    if (selectedIndex < 0) return;
    const target = direction === 'up' ? selectedIndex - 1 : selectedIndex + 1;
    if (target < 0 || target >= tabRows.length) return;

    setTabRows((prev) => {
      const updated = [...prev];
      [updated[selectedIndex], updated[target]] = [updated[target], updated[selectedIndex]];
      return updated.map((row, index) => ({ ...row, DisplayOrder: index + 1 }));
    });
    setOrderDirty(true);
  }, [selectedIndex, tabRows.length]);

  const conditionalRowStyles = useMemo(
    () => [
      {
        when: (row) => row.TabKey === selectedTabKey,
        style: {
          backgroundColor: 'rgba(18, 97, 160, 0.12)',
          borderLeft: '3px solid #1261A0',
        },
      },
    ],
    [selectedTabKey],
  );

  const columns = useMemo(() => [
    {
      name: (
        <div className="titlename">
          <span>Tab</span>
        </div>
      ),
      cell: (row) => (
        <div onMouseDown={() => selectRow(row.TabKey)}>
          <div className="fw-semibold">{getTabTypeLabel(row.TabKey, tabTypes) || row.TabKey}</div>
          {getTabTypeHelp(row.TabKey, tabTypes) || row.HelpText ? (
            <div className="text-muted small">{getTabTypeHelp(row.TabKey, tabTypes) || row.HelpText}</div>
          ) : null}
        </div>
      ),
      sortable: false,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Title</span>
        </div>
      ),
      cell: (row) => (
        <input
          type="text"
          className="form-control form-control-sm"
          value={row.TabTitle || ''}
          disabled={readOnly}
          placeholder={getTabTypeLabel(row.TabKey, tabTypes) || row.TypeDefaultTitle || row.TabKey}
          onChange={(e) => handleTitleChange(row.TabKey, e.target.value)}
          onMouseDown={() => selectRow(row.TabKey)}
          onFocus={() => selectRow(row.TabKey)}
        />
      ),
      sortable: false,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Show</span>
        </div>
      ),
      cell: (row) => {
        const toggleId = `detail-tab-visible-${row.TabKey}`;
        if (readOnly) {
          return (
            <span className={`badge ${row.IsVisible ? 'bg-success' : 'bg-secondary'}`}>
              {row.IsVisible ? 'Yes' : 'No'}
            </span>
          );
        }
        return (
          <div
            className="status-toggle modal-status"
            onMouseDown={() => selectRow(row.TabKey)}
          >
            <input
              type="checkbox"
              id={toggleId}
              className="check"
              checked={!!row.IsVisible}
              onChange={(e) => handleVisibleChange(row.TabKey, e.target.checked)}
            />
            <label htmlFor={toggleId} className="checktoggle" />
          </div>
        );
      },
      width: '110px',
      center: true,
      sortable: false,
      sortIcon: <SortArrow />,
    },
  ], [readOnly, tabTypes, handleVisibleChange, handleTitleChange, selectRow]);

  return (
    <>
      <p className="text-muted small mb-3">
        Use Show to control whether each tab appears on the sk-cart product detail page.
        New tab types are listed with Show off until you enable them here.
        Select a row, then use the arrows to reorder. Changes are saved when you click Submit.
      </p>

      <div className="d-flex align-items-stretch gap-2 sk-product-specs-grid-row">
        <div className="flex-grow-1 min-width-0 card table-list-card sk-product-specs-table mb-0">
          <div className="card-body">
            <DataTable
              className="table-responsive product-list"
              columns={columns}
              data={tabRows}
              keyField="TabKey"
              pagination={false}
              fixedHeader
              fixedHeaderScrollHeight="280px"
              sortIcon={<SortArrow />}
              persistTableHead
              conditionalRowStyles={conditionalRowStyles}
              onRowClicked={(row) => setSelectedTabKey(row.TabKey)}
              pointerOnHover
              highlightOnHover
              noDataComponent={<span className="text-muted">No detail page tabs configured.</span>}
            />
          </div>
        </div>
        {!readOnly ? (
          <div className="sk-spec-order-controls d-flex flex-column justify-content-center gap-2">
            <button
              type="button"
              className="sk-order-btn btn btn-outline-primary"
              disabled={!canMoveUp}
              title="Move selected up"
              aria-label="Move selected up"
              onClick={() => moveSelected('up')}
            >
              <Icons.ChevronUp size={20} />
            </button>
            <button
              type="button"
              className="sk-order-btn btn btn-outline-primary"
              disabled={!canMoveDown}
              title="Move selected down"
              aria-label="Move selected down"
              onClick={() => moveSelected('down')}
            >
              <Icons.ChevronDown size={20} />
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}

export default ProductDetailTabsTab;
